import { memo, useCallback, useMemo, type PropsWithChildren, type ReactNode } from 'react'
import { SectionList, View, type SectionListData } from 'react-native'

import { PlaceholderLine } from '@/src/elements/placeholder'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { Typography } from '@/src/elements/text/Typography'
import { EVENT_ORIGIN } from '@/src/features/community/models/community.constants'
import { SuggestedCommunitiesSection } from '@/src/features/community/ui/components/SuggestedCommunitiesSection'
import { FEED_ITEM_TYPE } from '@/src/features/prayer-space/models/prayerSpace.constants'
import { useInfiniteFeed } from '@/src/features/prayer-space/services/useInfiniteFeed'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { ScrollListenerProvider } from '@/src/general/components/ScrollListenerProvider'
import { TEST_ID } from '@/src/general/constants/testId'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { getFormattedSectionTitle, type TSection } from '@/src/general/utils/sectionizeInfiniteData'
import { useTranslate } from '@/src/lang/useTranslate'

import '@/src/features/publication/ui/screens/PublicationScreen/components/ProxyContent'

import type { TFeedItemApi } from '../../../api/feedApi.types'
import { PapaStyle } from '../../components/PapaStyle'
import EmptyPrayerSpace from './components/EmptyPrayerSpace'
import { FeedItemPlaceholder } from './components/FeedItemPlaceholder'
import { PrayerSpaceHeader } from './components/PrayerSpaceHeader'
import { PrayerSpaceItem } from './components/PrayerSpaceItem'

const ColumnWrapper = ({ children }: PropsWithChildren): ReactNode => (
  <View className="w-screen items-center">
    <View className="w-screen  md:max-w-screen-md">{children}</View>
  </View>
)

const ListHeader = memo(
  ({
    section,
    showCommunitiesSuggestion,
  }: {
    section: SectionListData<TFeedItemApi, TSection<TFeedItemApi>>
    showCommunitiesSuggestion: boolean
  }) => {
    const { t } = useTranslate()
    return (
      <ColumnWrapper>
        {/* Display suggestion after the today section */}
        {showCommunitiesSuggestion && (
          <View className="pt-4">
            <SuggestedCommunitiesSection
              containerProps={{ className: '!mb-0' }}
              scrollViewProps={{ role: 'article' }}
              sectionTitle={t('community.communities.suggested')}
              source={TRACKING_SOURCE.PRAYER_SPACE}
              origin={EVENT_ORIGIN.COMMUNITIES_SUGGESTION}
            />
          </View>
        )}
        <View className="mb-4 mt-8 w-screen rounded-md px-6 md:max-w-screen-md md:px-9">
          <Typography size="lg" colors="default" contrast="low" heading="h2" className="font-black">
            {getFormattedSectionTitle(section, t)}
          </Typography>
        </View>
      </ColumnWrapper>
    )
  },
)

export const PrayerSpaceScreen = (): ReactNode => {
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isPending, error } = useInfiniteFeed()
  const feedData = data?.transformedSections

  const formattedFeedDataWithoutWelcome = useMemo(() => {
    if (hasNextPage) return feedData

    if (feedData && feedData.length > 0) {
      const lastSection = feedData[feedData.length - 1]
      const hasLastSectionMultipleItems = lastSection.data.length > 1
      const isLastItemWelcome = lastSection.data[lastSection.data.length - 1].type === FEED_ITEM_TYPE.WELCOME
      if (!isLastItemWelcome) return feedData
      if (hasLastSectionMultipleItems) {
        const updatedLastSectionData = lastSection.data.slice(0, -1)
        return [...feedData.slice(0, -1), { ...lastSection, data: updatedLastSectionData }]
      } else {
        return feedData.slice(0, -1)
      }
    }
  }, [feedData, hasNextPage])

  const { t } = useTranslate()

  const isEmpty =
    (formattedFeedDataWithoutWelcome && formattedFeedDataWithoutWelcome.length === 0) || feedData?.length === 0

  const shouldDisplaySuggestedCommunities =
    formattedFeedDataWithoutWelcome && formattedFeedDataWithoutWelcome.length >= 2

  const ListEmptyComponent = () => {
    if (isPending) {
      return (
        <ColumnWrapper>
          <View className="mb-4 flex-1">
            <PlaceholderLine contrast="light" className="mb-4 ml-4 mt-12 h-7 max-w-40" size="lg" />
            <FeedItemPlaceholder />
          </View>
        </ColumnWrapper>
      )
    }
    if (error) {
      return (
        <ColumnWrapper>
          <ErrorMessage defaultErrorKey="common.error.unknown" error={error} />
        </ColumnWrapper>
      )
    }
    if (isEmpty) {
      return (
        <ColumnWrapper>
          <EmptyPrayerSpace />
        </ColumnWrapper>
      )
    }
    return null
  }

  const sections = useMemo(() => formattedFeedDataWithoutWelcome || [], [formattedFeedDataWithoutWelcome])
  const keyExtractor = useCallback((item: TFeedItemApi) => `item-${item.id}`, [])

  const ListFooterComponent = () => {
    return (
      <ColumnWrapper>
        {isFetchingNextPage && !isEmpty ? (
          <FeedItemPlaceholder />
        ) : (
          !shouldDisplaySuggestedCommunities &&
          !error &&
          !isEmpty && (
            <SuggestedCommunitiesSection
              containerProps={{ className: '!mb-0' }}
              scrollViewProps={{ role: 'article' }}
              sectionTitle={t('community.communities.suggested')}
              source={TRACKING_SOURCE.PRAYER_SPACE}
              origin={EVENT_ORIGIN.COMMUNITIES_SUGGESTION}
            />
          )
        )}
        <Spacer y={56} />
      </ColumnWrapper>
    )
  }
  return (
    <PapaStyle testID={TEST_ID.PRAYER_SPACE.SCREEN}>
      <View key="prayer-space-list" className="min-h-screen min-w-full flex-1 items-center">
        <ScrollListenerProvider<SectionList<TFeedItemApi, TSection<TFeedItemApi>>>>
          {({ ref, onScroll }) => (
            <SectionList
              ref={ref}
              onScroll={onScroll}
              role="feed"
              nestedScrollEnabled
              accessible
              scrollEventThrottle={50}
              testID={TEST_ID.PRAYER_SPACE.FEED_ITEM_LIST}
              className="max-w-full"
              sections={sections}
              stickySectionHeadersEnabled={false}
              initialNumToRender={10}
              keyExtractor={keyExtractor}
              ListHeaderComponent={PrayerSpaceHeader}
              renderSectionHeader={({ section }) => {
                return (
                  <ListHeader
                    section={section}
                    showCommunitiesSuggestion={data?.mapSectionIndex[section.title] === 1}
                  />
                )
              }}
              ListEmptyComponent={ListEmptyComponent}
              ItemSeparatorComponent={() => <View className="h-4" />}
              renderItem={({ item, index }) => (
                <ColumnWrapper>
                  <View className="px-2 md:px-5" role="article">
                    <PrayerSpaceItem item={item} index={index} />
                  </View>
                </ColumnWrapper>
              )}
              onEndReached={() => {
                if (hasNextPage && !isFetchingNextPage) fetchNextPage()
              }}
              onEndReachedThreshold={0.3}
              ListFooterComponent={ListFooterComponent}
            />
          )}
        </ScrollListenerProvider>
      </View>
    </PapaStyle>
  )
}
